import React, { useMemo, useRef, useState, useEffect } from 'react';
import { renderMarkdown } from '../../markdown';

/*
 * Assessment components (registry §3). Each receives:
 *   instance = {component_id, question_id, props, status}
 *   emit(eventType, payload)  — builds+sends the UIEvent (TutorMode binds ids)
 *   busy — a turn is streaming; inputs disable
 * Reveal data (correct_ids, option_explanations, point_results, canonical answers)
 * arrives ONLY via update patches after submission — initial props never have it.
 */

// Concept / difficulty / depth chips under a question prompt, plus the AI
// Selector's pick tag ("Weak spot", "Revision"…) and its learner-facing why.
// A raw dotted concept id (ai.rag.cs.types) never reaches the learner — the
// last segment is prettified into a readable label.
const prettyTag = (t) => (/^[a-z0-9_]+(\.[a-z0-9_]+)+$/.test(String(t))
  ? String(t).split('.').pop().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  : t);
export const Tags = ({ tags, tag, why }) => (
  ((tags || []).length || tag || why) ? (
    <div className="tu-tagblock">
      <div className="tu-tags">
        {tag && <span className="tu-tag-pill picked">◈ {tag}</span>}
        {(tags || []).slice(0, 4).map((t, i) => (
          <span key={t} className={`tu-tag-pill t${i % 4}`}>{prettyTag(t)}</span>
        ))}
      </div>
      {why && <div className="tu-why">{why}</div>}
    </div>
  ) : null
);

// Quiet always-available actions on an active question: an honest "I don't
// know" (counts as a miss, reveals + teaches) and "Show answer" (peek —
// recorded as skipped, resurfaces later).
const CardActions = ({ emit, busy }) => (
  <span className="tu-quiet-row">
    <button type="button" className="tu-quietbtn" disabled={busy}
      onClick={() => emit('dont_know', {})}>I don't know</button>
    <button type="button" className="tu-quietbtn" disabled={busy}
      onClick={() => emit('reveal_requested', {})}>Show answer</button>
  </span>
);

// A small "you were right/wrong" flash shown the instant the server's ack
// arrives (grading is code-side, ~ms) — no waiting for the coach's prose.
const VerdictFlash = ({ verdict }) => (
  (verdict && verdict.correct != null) ? (
    <div className={`tu-verdict${verdict.correct ? ' ok' : ''}`} role="status">
      {verdict.correct ? '✓ Correct' : '✗ Not quite'}
      {typeof verdict.score === 'number' && verdict.score > 1 ? ` · ${verdict.score}%` : ''}
    </div>
  ) : null
);

// --------------------------- mcq_card ----------------------------------------
// Options render in the ORDER GIVEN — no client shuffle, so option letters
// always read A/B/C/D top-to-bottom and match the coach's references.
export const TutorMcqCard = ({ instance, emit, busy }) => {
  const { props, status, verdict } = instance;
  const [selected, setSelected] = useState([]);
  const options = useMemo(() => props.options || [], [props.options]);
  const revealed = Array.isArray(props.correct_ids);
  const locked = status !== 'active' || busy;

  const toggle = (id) => {
    if (locked) return;
    setSelected((prev) => (props.multi_select
      ? (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])
      : [id]));
  };
  const submit = () => {
    if (locked || !selected.length) return;
    emit('answer_submitted', { selected_ids: selected });
  };

  return (
    <div className="tu-card tu-mcq" data-status={status}>
      <div className="tu-prompt">{renderMarkdown(props.prompt_md || '')}</div>
      <Tags tags={props.tags} tag={props.tag} why={props.why} />
      <div className="tu-opts" role="group" aria-label="Answer options">
        {options.map((o) => {
          const isSel = selected.includes(o.id);
          // Reveal patch marks the full key; before that, the instant ack
          // verdict colors ONLY the learner's own pick (no answer leak).
          const isCorrect = revealed
            ? (props.correct_ids || []).includes(o.id)
            : (verdict && isSel && verdict.correct);
          const isWrong = revealed ? (isSel && !isCorrect) : (verdict && isSel && !verdict.correct);
          const expl = revealed && (props.option_explanations || {})[o.id];
          return (
            <div key={o.id} className="tu-optwrap">
              <button
                type="button"
                className={`tu-opt${isSel ? ' sel' : ''}${isCorrect ? ' correct' : ''}${isWrong ? ' wrong' : ''}`}
                aria-pressed={isSel}
                disabled={locked}
                onClick={() => toggle(o.id)}
              >
                <span className="tu-optid">{o.id}</span>
                <span className="tu-opttext">{renderMarkdown(o.text_md || '')}</span>
              </button>
              {expl && <div className="tu-optexpl">{renderMarkdown(expl)}</div>}
            </div>
          );
        })}
      </div>
      {!revealed && status === 'active' && (
        <div className="tu-cardactions">
          <button type="button" className="tu-primary" disabled={busy || !selected.length} onClick={submit}>
            Submit answer
          </button>
          <CardActions emit={emit} busy={busy} />
        </div>
      )}
      {!revealed && status !== 'active' && <VerdictFlash verdict={verdict} />}
    </div>
  );
};

// --------------------------- flash_card --------------------------------------
export const TutorFlashCard = ({ instance, emit, busy }) => {
  const { props, status } = instance;
  const [flipped, setFlipped] = useState(false);
  const flippedOnce = useRef(false);

  const flip = () => {
    setFlipped((f) => !f);
    if (!flippedOnce.current) {
      flippedOnce.current = true;
      emit('card_flipped', { ms_to_flip: null });   // telemetry only
    }
  };
  const grades = [['again', '#ef4444'], ['hard', '#f59e0b'], ['good', '#10b981'], ['easy', '#3b82f6']];

  return (
    <div className="tu-card tu-flash" data-status={status}>
      {props.deck && <div className="tu-deckpos">{props.deck.index}/{props.deck.total}</div>}
      <button type="button" className={`tu-flashface${flipped ? ' back' : ''}`} onClick={flip}
        aria-label={flipped ? 'Show front' : 'Reveal answer'}>
        {renderMarkdown(flipped ? (props.back_md || '') : (props.front_md || ''))}
        {!flipped && <span className="tu-fliphint">Click to reveal</span>}
      </button>
      {flipped && status === 'active' && (
        <div className="tu-rate">
          {grades.map(([g, c]) => (
            <button key={g} type="button" className="tu-ratebtn" style={{ '--rc': c }} disabled={busy}
              onClick={() => emit('self_graded', { grade: g })}>
              {g}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// --------------------------- rapid_answer ------------------------------------
export const RapidAnswer = ({ instance, emit, busy }) => {
  const { props, status, verdict } = instance;
  const [text, setText] = useState('');
  const [left, setLeft] = useState(props.timer_s || 0);
  const doneRef = useRef(false);
  const locked = status !== 'active' || busy;

  const submit = (timedOut) => {
    if (doneRef.current || instance.status !== 'active') return;
    doneRef.current = true;
    emit('answer_submitted', { text, timed_out: !!timedOut });
  };

  useEffect(() => {
    if (!props.timer_s || locked) return undefined;
    const id = setInterval(() => setLeft((s) => {
      if (s <= 1) {
        clearInterval(id);
        if (props.auto_submit_on_timeout !== false) submit(true);
        else emit('timer_expired', {});
        return 0;
      }
      return s - 1;
    }), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance.component_id]);

  return (
    <div className="tu-card tu-rapid" data-status={status}>
      <div className="tu-prompt">{renderMarkdown(props.prompt_md || '')}</div>
      <Tags tags={props.tags} tag={props.tag} why={props.why} />
      {props.timer_s > 0 && status === 'active' && (
        <div className={`tu-timer${left <= 5 ? ' low' : ''}`}>{left}s</div>
      )}
      <div className="tu-rapidrow">
        <input
          className="tu-input"
          type={(props.input || {}).type === 'number' ? 'number' : 'text'}
          maxLength={(props.input || {}).max_chars || undefined}
          placeholder={(props.input || {}).placeholder || 'Your answer…'}
          value={text}
          disabled={locked}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(false); }}
        />
        <button type="button" className="tu-primary" disabled={locked || !text.trim()} onClick={() => submit(false)}>
          Go
        </button>
      </div>
      {status === 'active' && <CardActions emit={emit} busy={busy} />}
      {!props.canonical_answer_md && status !== 'active' && <VerdictFlash verdict={verdict} />}
      {props.canonical_answer_md && (
        <div className="tu-canonical">{renderMarkdown(props.canonical_answer_md)}</div>
      )}
    </div>
  );
};

// --------------------------- structured_answer -------------------------------
const POINT_ICON = { covered: '✓', partial: '◐', missed: '✗' };

export const StructuredAnswer = ({ instance, emit, busy }) => {
  const { props, status, verdict } = instance;
  const results = {};
  (props.point_results || []).forEach((r) => { results[r.id] = r; });
  // The answer structure hides behind a dropdown while answering — recall
  // first, peek at the skeleton only when you want it. Grading opens it
  // automatically so the per-point ✓/✗ marks are visible.
  const [structureOpen, setStructureOpen] = useState(false);
  const showPoints = structureOpen || !!props.point_results;

  return (
    <div className="tu-card tu-structured" data-status={status}>
      <div className="tu-prompt">{renderMarkdown(props.prompt_md || '')}</div>
      <Tags tags={props.tags} tag={props.tag} why={props.why} />
      {(props.talking_points || []).length > 0 && (
        <div className="tu-structure">
          {!props.point_results && (
            <button type="button" className="tu-structure-toggle" aria-expanded={structureOpen}
              onClick={() => setStructureOpen((o) => !o)}>
              Answer structure <span className={`tu-structure-caret${structureOpen ? ' open' : ''}`}>▾</span>
            </button>
          )}
          {showPoints && (
            <ul className="tu-points">
              {props.talking_points.map((p) => {
                const r = results[p.id];
                return (
                  <li key={p.id} className={`tu-point${r ? ` ${r.status}` : ''}`}>
                    <span className="tu-pointmark">{r ? POINT_ICON[r.status] || '•' : '•'}</span>
                    <span>{p.label}{r && r.evidence ? <em className="tu-evidence"> — {r.evidence}</em> : null}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      {status === 'active' && (
        <>
          <div className="tu-answernote">Answer in the chat box below ↓ — open “Answer structure” if you want the skeleton, or dictate with the mic.</div>
          <CardActions emit={emit} busy={busy} />
        </>
      )}
      {status !== 'active' && !props.point_results && (
        verdict ? <VerdictFlash verdict={verdict} /> : <div className="tu-substate">Submitted — grading…</div>
      )}
    </div>
  );
};
